import React from 'react';
import { Button, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from 'theme';

const spotSize = 60;

const Container = styled(Box)(({ theme }) => ({
    position: 'relative',
    zIndex: theme.zIndex.appBar + 300,
}));

const CircularBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    width: `${spotSize}px`,
    height: `${spotSize}px`,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${theme.palette.primary.main} 7%, ${theme.palette.secondary.main} 20%, transparent 60%)`,
}));

interface ButtonsContProps {
    buttons: ButtonProps[]
}

interface ButtonProps {
    label: string;
    onClick: () => void;
    color?: 'primary' | 'secondary';
}

const ButtonsAtAngle = (props: ButtonsContProps) => {
    const iniAngle = props.buttons.length === 3 ? 30 : 0;
    const distance = 40;
    const threshold = 10; //px
    return <>
        {
            props.buttons.map((bp, index) => {
                const radians = (((360 / props.buttons.length) * index + iniAngle) * Math.PI) / 180;
                const yCoord = -1 * distance * Math.sin(radians);
                const xCoord = distance * Math.cos(radians);

                const extraProps: {
                    bottom?: string;
                    top?: string;
                    right?: string;
                    left?: string;
                    transform?: string;
                } = {};

                if (!(yCoord < threshold && -1 * threshold < yCoord)) {
                    if (yCoord < 0) {
                        extraProps.bottom = '0';
                    } else {
                        extraProps.top = '0';
                    }
                } else {
                    extraProps.transform = 'translateY(-50%)';
                }

                if (!(xCoord < threshold && -1 * threshold < xCoord)) {
                    if (xCoord < 0) {
                        extraProps.right = '0';
                    } else {
                        extraProps.left = '0';
                    }
                } else {
                    extraProps.transform = 'translateX(-50%)';
                }

                const { onClick, label, color } = bp;
                return (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: `${yCoord}px`,
                            left: `${xCoord}px`,
                        }}
                    >
                        <Button
                            color={color ?? 'primary'}
                            variant='contained'
                            onClick={onClick}
                            sx={{
                                position: 'absolute',
                                whiteSpace: 'nowrap',
                                ...extraProps,
                            }}
                        >
                            {label}
                        </Button>
                    </Box>
                );
            })
        }
    </>
}

const ButtonsList = (props: ButtonsContProps) => {
    const distance = 65;
    return <>
        {
            props.buttons.map((bp, index) => {
                const { onClick, label, color } = bp;
                return (
                    <Box
                        sx={{
                            position: 'absolute',
                        }}
                    >
                        <Button
                            color={color ?? 'primary'}
                            variant='contained'
                            onClick={onClick}
                            sx={{
                                bottom: index !== (props.buttons.length - 1) ? `${index * distance + 30}px` : 'auto',
                                top: index !== (props.buttons.length - 1) ? 'auto' : '30px',
                                position: 'absolute',
                                whiteSpace: 'nowrap',
                                transform: 'translateX(-50%)',
                            }}
                        >
                            {label}
                        </Button>
                    </Box>
                );
            })
        }
    </>
}

const PlaceClick = (props: { addNew: () => void; setHome: () => void; center: () => void; close: () => void }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const buttonsProps: Array<ButtonProps> = [];
    if (props.setHome) {
        buttonsProps.push({
            label: 'Set Home',
            onClick: () => props.setHome(),
        });
    }
    if (props.addNew) {
        buttonsProps.push({
            label: 'Add spot here',
            onClick: () => props.addNew(),
        });
    }
    buttonsProps.push({
        label: 'Center map',
        onClick: () => {
            if (props.center) {
                props.center();
            }
        },
    });
    buttonsProps.push({
        label: 'Close',
        color: 'secondary',
        onClick: () => {
            if (props.close) {
                props.close();
            }
        },
    });

    return (
        <Container>
            <Box>
                {isMobile ?
                    <ButtonsList buttons={buttonsProps}/> :
                    <ButtonsAtAngle buttons={buttonsProps}/>}
            </Box>
            <CircularBox></CircularBox>
        </Container>
    );
};

export default PlaceClick;
